import { __rest } from '../../../_virtual/_tslib.js';
import React from 'react';
import PropTypes from 'prop-types';
import HoneyBem from '../../../packages/honey-bem/honey-bem.js';
import * as reactUtils from '../../../packages/react-utils/react-utils.js';
import * as propsUtils from '../../../packages/props-utils/props-utils.js';
import { InnerRefPropType } from '../../../packages/props-utils/prop-types.js';
import { InlineLabel } from '../../../components/_atoms/inline-label/inline-label.js';
import { InputField } from '../../../components/_atoms/input-field/input-field.js';
import { InputInfoChildren, InputInfo } from '../../../components/_atoms/input-info/input-info.js';
import '../../../components/_atoms/nav-elem/nav-elem.js';
import { iconValidator, handleIcon } from '../../../components/input/input-utils.js';
import { useComponentId } from '../../../hooks/_use-component-id/use-component-id.js';
import { useAutocompleteContext } from '../state/autocomplete-context.js';
import { ComboboxTags } from './quarks/combobox-tags.js';
import { ComboboxControllers } from './quarks/combobox-controllers.js';
import { inputLabelValidator } from '../../../prop-types-validators/input-label-validator.js';
import { ListBoxOptionPropType } from '../list-box/quarks/prop-types.js';
import styles from './combobox.scss.js';
import { Injecss } from '../../../packages/injecss/injecss.js';

const inputBem = new HoneyBem('input', styles);
const comboboxBem = new HoneyBem('combobox', styles);
const comboboxElement = e => "".concat(inputBem.e(e), " ").concat(comboboxBem.e(e));
/** @since 12.0.0 */
function Combobox(_a) {
  var {
      id,
      extraClasses,
      innerRef,
      containerRef,
      inputFieldRef: inputFieldRefProp,
      label,
      value,
      defaultValue,
      selectedOptions,
      limitSelectedOptions,
      loading = false,
      disabled = false,
      valid,
      info,
      infoError,
      iconLeft,
      iconRight,
      inlineLabelLeft,
      inlineLabelRight,
      resetProps,
      toggleProps,
      tagProps,
      expanded = false,
      multiple = false,
      'aria-describedby': ariaDescribedBy,
      srOnlyInfo
    } = _a,
    rest = __rest(_a, ["id", "extraClasses", "innerRef", "containerRef", "inputFieldRef", "label", "value", "defaultValue", "selectedOptions", "limitSelectedOptions", "loading", "disabled", "valid", "info", "infoError", "iconLeft", "iconRight", "inlineLabelLeft", "inlineLabelRight", "resetProps", "toggleProps", "tagProps", "expanded", "multiple", 'aria-describedby', "srOnlyInfo"]);
  const inputFieldRef = React.useRef(null);
  const componentId = id || useComponentId('hcr-combobox');
  const autocompleteContext = useAutocompleteContext();
  const {
    dispatch
  } = autocompleteContext ? Object.assign({}, autocompleteContext) : {};
  /** Dispatches expanded state if is inside an autocomplete context. */
  const toggleExpanded = event => {
    if (!dispatch) return;
    if (expanded) {
      dispatch({
        type: 'COLLAPSE',
        latestEvent: event
      });
    } else {
      dispatch({
        type: 'EXPAND',
        latestEvent: event
      });
    }
  };
  const hasDisplayedValue = value && value !== '' || defaultValue && defaultValue !== '';
  const hasSelectedOptions = selectedOptions && selectedOptions.length > 0;
  const hasSingleValue = !multiple && hasDisplayedValue;
  const hasMultiSelection = multiple && (hasSelectedOptions || hasDisplayedValue);
  const shouldShowResetButton = resetProps && (hasMultiSelection || hasSingleValue);
  const resetControllerProps = shouldShowResetButton ? Object.assign(Object.assign({}, resetProps), {
    'aria-label': resetProps.label,
    onClick: event => {
      var _a, _b;
      (_a = resetProps.onClick) === null || _a === void 0 ? void 0 : _a.call(resetProps, event);
      if (dispatch && !event.preventHCREventHandling) {
        dispatch({
          type: 'RESET',
          latestEvent: event
        });
        (_b = inputFieldRef.current) === null || _b === void 0 ? void 0 : _b.focus();
      }
    }
  }) : undefined;
  const popupControllerProps = toggleProps && Object.assign(Object.assign({}, toggleProps), {
    'aria-label': toggleProps.label,
    'aria-expanded': expanded,
    onClick: event => {
      var _a;
      (_a = toggleProps.onClick) === null || _a === void 0 ? void 0 : _a.call(toggleProps, event);
      if (!event.preventHCREventHandling) {
        toggleExpanded(event);
      }
    }
  });
  const hasControllers = resetControllerProps || popupControllerProps;
  const inputClassNames = inputBem.b({
    'has-controllers': hasControllers,
    error: !loading && valid === false,
    valid: !loading && valid === true,
    loading,
    disabled
  });
  const hasTags = multiple && selectedOptions && selectedOptions.length > 0;
  const comboboxClassName = comboboxBem.b({
    'has-controllers': hasControllers,
    'has-tags': hasTags,
    error: !loading && valid === false,
    valid: !loading && valid === true,
    loading,
    disabled
  });
  const labelId = "label-".concat(componentId);
  const tagsId = "tag-".concat(componentId);
  const selectedOptionsIds = multiple && selectedOptions ? selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.map(option => "".concat(tagsId, "-").concat(option.value)).join(' ') : '';
  const describedByIds = propsUtils.handleAriaDescribedBy(componentId, {
    inlineLabelLeft,
    inlineLabelRight,
    info,
    srOnlyInfo: true,
    ariaDescribedBy
  }) || '';
  /* `tabIndex` in the `__container` allows the elements to be captured as relatedTarget
   * by the Autocomplete onBlur handler. */
  return /*#__PURE__*/React.createElement("div", {
    ref: innerRef,
    className: "".concat(inputClassNames, " ").concat(comboboxClassName, " ").concat(extraClasses || '')
  }, label && propsUtils.handleLabel({
    label,
    inputId: componentId,
    id: labelId,
    bem: comboboxBem
  }), /*#__PURE__*/React.createElement("div", {
    role: "presentation",
    ref: containerRef,
    className: comboboxElement('container'),
    tabIndex: -1,
    onClick: () => {
      var _a;
      (_a = inputFieldRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
  }, inlineLabelLeft && (/*#__PURE__*/React.createElement(InlineLabel, {
    id: "left-".concat(componentId),
    className: comboboxElement('inline-label')
  }, inlineLabelLeft)), iconLeft && handleIcon(iconLeft, comboboxElement('icon')), /*#__PURE__*/React.createElement("div", {
    className: comboboxBem.e('container-inner')
  }, hasTags && (/*#__PURE__*/React.createElement(ComboboxTags, {
    id: tagsId,
    selectedOptions: selectedOptions,
    limitSelectedOptions: limitSelectedOptions,
    tagProps: tagProps,
    expanded: expanded,
    disabled: disabled
  })), /*#__PURE__*/React.createElement(InputField, Object.assign({
    id: componentId,
    ref: element => {
      reactUtils.assignInnerRefWithComponentRef(element, inputFieldRefProp, inputFieldRef);
    },
    className: comboboxElement('field'),
    autoComplete: "off",
    role: "combobox",
    "aria-haspopup": "listbox",
    "aria-autocomplete": "list",
    "aria-expanded": expanded,
    "aria-describedby": "".concat(describedByIds, " ").concat(selectedOptionsIds),
    "aria-errormessage": "infoError-".concat(componentId),
    value: value,
    defaultValue: defaultValue,
    busy: loading,
    valid: valid,
    disabled: disabled
  }, rest, {
    onMouseDown: event => {
      var _a;
      (_a = rest.onMouseDown) === null || _a === void 0 ? void 0 : _a.call(rest, event);
      /**
       * if event.buttons === 1: left mouse button
       * if event.buttons === 2: right mouse button
       *
       * Run toggle only if it's left mouse button
       */
      if (!event.preventHCREventHandling && event.buttons !== 2) {
        toggleExpanded(event);
      }
    }
  }))), /*#__PURE__*/React.createElement("span", {
    className: comboboxElement('feedback-icon'),
    "aria-hidden": "true"
  }), iconRight && handleIcon(iconRight, comboboxElement('icon')), /*#__PURE__*/React.createElement(ComboboxControllers, {
    disabled: disabled,
    selectedOptions: selectedOptions,
    toggleProps: popupControllerProps,
    resetProps: resetControllerProps,
    className: comboboxElement('controllers')
  }), inlineLabelRight && (/*#__PURE__*/React.createElement(InlineLabel, {
    id: "right-".concat(componentId),
    className: comboboxElement('inline-label')
  }, inlineLabelRight))), /*#__PURE__*/React.createElement(InputInfo, {
    id: componentId,
    error: true,
    className: inputBem.e('info')
  }, valid === false ? infoError : ''), info && /*#__PURE__*/React.createElement(InputInfo, {
    id: componentId,
    className: inputBem.e('info')
  }, info), /*#__PURE__*/React.createElement(InputInfo, {
    "aria-live": "polite",
    id: componentId,
    srOnly: true,
    className: inputBem.e('info')
  }, srOnlyInfo));
}
Combobox.displayName = 'Combobox';
/** Combobox props allowed via Autocomplete `comboboxProps`. */
const AutocompleteComboboxPropTypes = {
  label: inputLabelValidator(),
  extraClasses: PropTypes.string,
  disabled: PropTypes.bool,
  valid: PropTypes.bool,
  loading: PropTypes.bool,
  info: InputInfoChildren,
  srOnlyInfo: InputInfoChildren,
  infoError: InputInfoChildren,
  iconLeft: iconValidator(),
  iconRight: iconValidator(),
  inlineLabelLeft: PropTypes.node,
  inlineLabelRight: PropTypes.node,
  resetProps: PropTypes.shape({
    onClick: PropTypes.func,
    label: PropTypes.string.isRequired
  }),
  toggleProps: PropTypes.shape({
    onClick: PropTypes.func,
    label: PropTypes.string.isRequired
  }),
  tagProps: PropTypes.func
};
Combobox.propTypes = Object.assign(Object.assign({}, AutocompleteComboboxPropTypes), {
  id: PropTypes.string,
  innerRef: InnerRefPropType,
  containerRef: InnerRefPropType,
  inputFieldRef: InnerRefPropType,
  selectedOptions: PropTypes.arrayOf(ListBoxOptionPropType)
});
var Combobox$1 = Injecss(Combobox, styles);

export { AutocompleteComboboxPropTypes, Combobox, Combobox$1 as default };
